import React from 'react';
import { Box, Typography } from '@mui/material';
import { ColumnMappingEntry, SupplyPreview } from '../../../services/benchmarkService';
import { TierPill } from '../BenchmarkPrimitives';
import { count, percent } from '../BenchmarkVocabulary';
import ColumnMappingTable from './ColumnMappingTable';
import { comparisonModeLabel } from './suppliedSampleCopy';
import { dsVar, ink } from '../../../theme/tokens';

const heading = {
    fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em',
    textTransform: 'uppercase' as const, color: dsVar('fg-muted'), mb: 1
};

/** A coverage entry's rate, whichever shape the loader recorded it in. */
function rateOf(entry: any): number | null {
    if (typeof entry === 'number') return entry;
    const rate = entry?.rate ?? entry?.coverage;
    return typeof rate === 'number' ? rate : null;
}

const humanise = (key: string) => key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/**
 * How the loader read a supplied sample, before anything is scored: the
 * column mapping, the tiers as the platform will read them, what evidence the
 * run will have, and the loader's warnings.
 *
 * Shown in full so the user sees how their file was understood before they
 * commit to a run that takes minutes.
 */
const SamplePreview: React.FC<{ preview: SupplyPreview }> = ({ preview }) => {
    const source = preview.sample_spec?.dataset_source ?? {};
    const mapping: ColumnMappingEntry[] = Object.entries(source.column_mapping ?? {}).map(([sourceColumn, target]) => ({
        source: sourceColumn,
        target,
        method: source.column_methods?.[sourceColumn] ?? null
    }));
    const rows = preview.rows ?? [];
    const coverage = Object.entries(preview.coverage ?? {})
        .map(([key, entry]) => ({ key, rate: rateOf(entry) }))
        .filter((c) => c.rate !== null);
    const kycSources = preview.coverage?.kyc?.sources as Record<string, number> | undefined;
    const labels = Object.entries(preview.labels_available ?? {});
    const warnings = preview.warnings ?? [];

    return (
        <Box className="flex flex-col gap-5">
            <Box className="flex flex-wrap gap-x-6 gap-y-1">
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-body') }}>
                    <Box component="span" sx={{ fontWeight: 700, color: dsVar('accent-fg') }}>{count(source.rows_supplied ?? rows.length)}</Box> alerts read
                </Typography>
                {preview.sessions !== undefined && (
                    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-body') }}>
                        <Box component="span" sx={{ fontWeight: 700, color: dsVar('accent-fg') }}>{count(preview.sessions)}</Box> cases to score
                    </Typography>
                )}
                {preview.comparison_mode && (
                    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>Mode: {comparisonModeLabel(preview.comparison_mode)}</Typography>
                )}
            </Box>

            {warnings.length > 0 && (
                <Box sx={{ p: 1.5, borderRadius: '8px', border: '1px solid rgba(251, 191, 36, 0.35)', backgroundColor: 'rgba(251, 191, 36, 0.06)' }}>
                    <Typography sx={{ ...heading, color: ink('#FBBF24') }}>What the loader noticed</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {warnings.map((w) => (
                            <Typography component="li" key={w} sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.6, mb: 0.5 }}>{w}</Typography>
                        ))}
                    </Box>
                </Box>
            )}

            <Box className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Box>
                    <Typography sx={heading}>How your columns were read</Typography>
                    <ColumnMappingTable entries={mapping} />
                    {(source.unmapped_columns?.length ?? 0) > 0 && (
                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 1 }}>
                            Not used: {source.unmapped_columns!.join(', ')}
                        </Typography>
                    )}
                    {(source.unmapped_dispositions?.length ?? 0) > 0 && (
                        <Typography sx={{ fontSize: '0.75rem', color: ink('#FBBF24'), mt: 0.5 }}>
                            Outcome wordings not understood: {source.unmapped_dispositions!.join(', ')}
                        </Typography>
                    )}
                </Box>

                <Box className="flex flex-col gap-4">
                    {coverage.length > 0 && (
                        <Box>
                            <Typography sx={heading}>Evidence the run will have</Typography>
                            {coverage.map((c) => (
                                <Box key={c.key} className="flex justify-between gap-3" sx={{ py: 0.25 }}>
                                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>{humanise(c.key)}</Typography>
                                    <Typography sx={{ fontSize: '0.8125rem', color: (c.rate as number) < 0.5 ? ink('#FBBF24') : ink('#10B981'), fontWeight: 600 }}>
                                        {percent(c.rate, 0)}
                                    </Typography>
                                </Box>
                            ))}
                            {kycSources && (
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.75 }}>
                                    KYC per customer: {count(kycSources.supplied ?? 0)} supplied · {count(kycSources.customer_master ?? 0)} from your customer records · {count(kycSources.none ?? 0)} none
                                </Typography>
                            )}
                        </Box>
                    )}
                    {labels.length > 0 && (
                        <Box>
                            <Typography sx={heading}>Outcomes available to measure against</Typography>
                            {labels.map(([family, info]) => (
                                <Box key={family} className="flex justify-between gap-3" sx={{ py: 0.25 }}>
                                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>{humanise(family)}</Typography>
                                    <Typography sx={{ fontSize: '0.8125rem', color: info?.available ? ink('#10B981') : dsVar('fg-muted') }}>
                                        {info?.available ? (typeof info.count === 'number' ? `${count(info.count)} alerts` : 'Available') : 'None'}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>

            {rows.length > 0 && (
                <Box>
                    <Typography sx={heading}>
                        Sampled alerts{preview.rows_listed && source.rows_supplied && preview.rows_listed < source.rows_supplied
                            ? ` (first ${count(preview.rows_listed)})` : ''}
                    </Typography>
                    <Box sx={{ maxHeight: 280, overflowY: 'auto', border: `1px solid ${dsVar('line')}`, borderRadius: '8px' }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                            <Box component="thead" sx={{ position: 'sticky', top: 0, backgroundColor: dsVar('surface') }}>
                                <Box component="tr">
                                    {['Alert', 'Scenario', 'Your tier', 'Read as', 'Outcome', 'SAR'].map((h) => (
                                        <Box component="th" key={h} sx={{ ...heading, mb: 0, textAlign: 'left', px: 1.25, py: 1 }}>{h}</Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {rows.map((row, i) => (
                                    <Box component="tr" key={`${row.alert_ref ?? i}`} sx={{ borderTop: `1px solid ${dsVar('line')}` }}>
                                        <Box component="td" sx={{ px: 1.25, py: 0.75, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', color: dsVar('fg-soft') }}>{row.alert_ref}</Box>
                                        <Box component="td" sx={{ px: 1.25, py: 0.75, fontSize: '0.75rem', color: dsVar('fg-2') }}>{row.scenario_id}</Box>
                                        <Box component="td" sx={{ px: 1.25, py: 0.75, fontSize: '0.75rem', color: dsVar('fg-2') }}>{row.priority ?? '—'}</Box>
                                        <Box component="td" sx={{ px: 1.25, py: 0.75 }}>
                                            {row.priority_mapped
                                                ? <TierPill code={row.priority_mapped} />
                                                : <Typography component="span" sx={{ fontSize: '0.75rem', color: ink('#FBBF24') }}>Not mapped</Typography>}
                                        </Box>
                                        <Box component="td" sx={{ px: 1.25, py: 0.75, fontSize: '0.75rem', color: dsVar('fg-2') }}>{row.disposition ? humanise(String(row.disposition)) : '—'}</Box>
                                        <Box component="td" sx={{ px: 1.25, py: 0.75, fontSize: '0.75rem', color: row.sar_filed ? ink('#FB923C') : dsVar('fg-muted') }}>
                                            {row.sar_filed === null || row.sar_filed === undefined ? '—' : row.sar_filed ? 'Yes' : 'No'}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default SamplePreview;
